import { Button as BaseButton } from "@base-ui/react/button";
import bellIcon from "@loot-os/icons/icons/bell";
import type { ReactNode } from "react";

import { Icon } from "../../core/Icon.js";
import { iconButtonStyles } from "../../core/IconButton.styles.js";
import { Popover } from "../../core/Popover.js";
import { Sheet } from "../../core/Sheet.js";
import { useRiseCount } from "../../core/useRiseCount.js";
import { useMediaQuery } from "../../foundations/useMediaQuery.js";

export interface NotificationBellProps {
  /** The bell's name, with the badge's own text when there is one: "Notifications, 9+ unread". */
  label: string;
  /** The badge's text ("3", "9+"), or null for none. It is decorative: `label` says it. */
  badge: string | null;
  /**
   * How many notifications are unread, or undefined while that is loading. The bell rings when it
   * rises; the count arriving is not a rise unless `ringOnLoad`.
   */
  unreadCount: number | undefined;
  /**
   * Rings once when the count first arrives with something unread. The app decides how often a
   * shopper hears it (once a browser session on the storefront); read only on that arrival.
   */
  ringOnLoad?: boolean;
  /** The panel's accessible name: "Notifications". */
  panelLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The panel, usually a NotificationPanel. `phone` is true when it fills the screen, where it
   * needs its own close button.
   */
  children: (opts: { phone: boolean }) => ReactNode;
}

/**
 * The header's bell and its unread badge. It opens the panel in a popover under it, or in a
 * full-screen sheet on a phone.
 */
export function NotificationBell({
  label,
  badge,
  unreadCount,
  ringOnLoad = false,
  panelLabel,
  open,
  onOpenChange,
  children,
}: NotificationBellProps) {
  const desktop = useMediaQuery("(min-width: 768px)");
  // The bell rings when the unread count rises, never when one is read.
  const rings = useRiseCount(unreadCount, { fromZero: ringOnLoad });
  const trigger = (
    <BaseButton
      aria-label={label}
      className={iconButtonStyles({ variant: "secondary", size: "sm", className: "relative" })}
    >
      <Icon icon={bellIcon} size={16} trigger="hover" playOn={rings} />
      {badge && (
        <span
          aria-hidden
          className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-pill bg-accent-default px-1 font-mono text-mono-meta leading-none text-text-on-accent tabular-nums ring-2 ring-bg-canvas"
        >
          {badge}
        </span>
      )}
    </BaseButton>
  );

  if (!desktop) {
    return (
      <Sheet
        title={panelLabel}
        bare
        side="right"
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        className="w-full rounded-none border-l-0"
      >
        {children({ phone: true })}
      </Sheet>
    );
  }
  return (
    <Popover
      trigger={trigger}
      label={panelLabel}
      open={open}
      onOpenChange={onOpenChange}
      className="max-h-[min(36rem,var(--available-height))] w-[380px]"
    >
      {children({ phone: false })}
    </Popover>
  );
}
